import { FullscreenPopup as FullscreenPopupConfig } from 'hooks/userPreferences/configs';
import { useLocalStorage } from 'hooks/userPreferences/useLocalStorage';
import { useCallback } from 'react';
import type { FullscreenPopupVariant } from 'types/cms/Globals';

const isTrackableVariant = (
  variant?: FullscreenPopupVariant,
): variant is Exclude<FullscreenPopupVariant, 'none'> =>
  !!variant && variant !== 'none';

/**
 * Interface over the shared `fullscreenPopup` localStorage config, which stores
 * the list of popup variants a user has already seen. Tracking is by variant and
 * shared across the nutrition and skincare sections, so a variant is only ever
 * shown once regardless of which section it appears on.
 */
export const useViewedVariants = () => {
  const { value, getValue, setStoredValue } = useLocalStorage(
    FullscreenPopupConfig,
  );

  /** Reactive check for render-time gating (SSR-safe). */
  const hasViewedVariant = useCallback(
    (variant?: FullscreenPopupVariant) =>
      isTrackableVariant(variant) && (value ?? []).includes(variant),
    [value],
  );

  /** Whether the popup for this variant should be shown, i.e. it is a real,
   * trackable variant that has not been viewed yet. */
  const shouldShowVariant = useCallback(
    (variant?: FullscreenPopupVariant) =>
      isTrackableVariant(variant) && !hasViewedVariant(variant),
    [hasViewedVariant],
  );

  /** Fresh read straight from storage, for use inside effects. */
  const getHasViewedVariant = useCallback(
    (variant?: FullscreenPopupVariant) =>
      isTrackableVariant(variant) && (getValue() ?? []).includes(variant),
    [getValue],
  );

  /** Records a variant as viewed. No-ops for absent/`none` variants. */
  const addViewedVariant = useCallback(
    (variant?: FullscreenPopupVariant) => {
      if (!isTrackableVariant(variant)) return;

      setStoredValue((prev) =>
        prev.includes(variant) ? prev : [...prev, variant],
      );
    },
    [setStoredValue],
  );

  return {
    hasViewedVariant,
    shouldShowVariant,
    getHasViewedVariant,
    addViewedVariant,
  };
};
